import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertCircle, ChevronLeft, ChevronRight, Flag, FlaskConical, Info, Landmark, Map as MapIcon, RefreshCw, Search, Swords,
  Trophy, Users, X, Zap, Building2,
} from 'lucide-react';
import { apiService } from '@/services/api.service';
import { supportsGameGroups } from '@/config/games';
import { useGameStore } from '@/stores/game.store';
import { useUsableGameGroups } from '@/stores/gameGroups.store';
import { gameGroupHeaderFor } from '@/utils/gameGroups';
import { BarList, ColumnChart, DonutChart, type ColumnDatum } from './JobsCharts';
import { dayLabel, formatInstant, shortDay } from './jobStats';
import { formatMoney, medal, pageLabel } from './moneyLeaderboard';
import { formatMoneyShort, leagueLabel } from './shopFactions';
import {
  RD_BRANCH_ORDER, type CountryPart, type CountrySort, countriesError, filterCountries, groupColor, perGroup,
  rdBranchColor, rdBranchLabel, sortCountries, tableRows, total, unionDates, warEndLabel, worldLabel,
} from './countries';
import lb from './MoneyLeaderboardSection.module.scss';
import styles from './JobsStatsSection.module.scss';

const PAGE = 25;
const BOTH = '*';

const SORTS: { key: CountrySort; label: string }[] = [
  { key: 'power', label: 'Power' },
  { key: 'members', label: 'Membres' },
  { key: 'claims', label: 'Territoire' },
  { key: 'rdLevel', label: 'R&D' },
  { key: 'bank', label: 'Banque' },
  { key: 'gdp', label: 'PIB' },
  { key: 'warsWon', label: 'Guerres gagnées' },
  { key: 'createdAt', label: 'Les plus récents' },
  { key: 'name', label: 'Nom' },
];

const LEAGUE_COLORS: Record<string, string> = {
  unranked: 'var(--job-other)', bronze: 'var(--job-2)', silver: 'var(--job-1)', gold: 'var(--job-4)',
  platinum: 'var(--job-3)', diamond: 'var(--job-7)',
};

interface GroupFailure { key: string; label: string; status?: number; message: string }

const int = (n: number) => Math.round(n).toLocaleString('fr-FR');
const dec = (n: number) => n.toLocaleString('fr-FR', { maximumFractionDigits: 1 });
const plural = (n: number, one: string, many: string) => `${int(n)} ${Math.round(n) > 1 ? many : one}`;
const countries = (n: number) => plural(n, 'pays', 'pays');
const players = (n: number) => plural(n, 'joueur', 'joueurs');
const claimsFmt = (n: number) => plural(n, 'revendication', 'revendications');
const membersFmt = (n: number) => plural(n, 'membre', 'membres');
/** Graduation d'un compte : les graduations entre deux entiers (petites valeurs) restent sans texte. */
const tick = (n: number) => (Number.isInteger(n) ? int(n) : '');

/**
 * Section « Pays » de l'espace Statistiques (NationsGlory) : pays fondés et libres, joueurs avec et sans pays, power,
 * territoire, banque, PIB, recherche et développement (R&D), ligues, guerres, sites UNESCO, créations, évolution et
 * tableau des pays. Lu par le satellite de chaque groupe (1.0.29) dans la base de sa NGCountryAPI ; « Les deux »
 * interroge Nova et Iris et les met côte à côte, chaque groupe dans sa couleur.
 */
export function CountriesSection() {
  const gameId = useGameStore((s) => s.activeGameId);
  const activeGroup = useGameStore((s) => gameGroupHeaderFor(s.activeGameId, s.gameGroupsKnown, s.activeGameGroupByGame));
  const usable = useUsableGameGroups(gameId);
  const groups = useMemo(() => (supportsGameGroups(gameId) ? usable.filter((g) => g.available && !g.retired) : []), [gameId, usable]);
  const [choice, setChoice] = useState<string | null>(null);
  const selected = choice ?? (activeGroup && groups.some((g) => g.key === activeGroup) ? activeGroup : groups[0]?.key ?? null);

  const [parts, setParts] = useState<CountryPart[] | null>(null);
  const [failures, setFailures] = useState<GroupFailure[]>([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState<CountrySort>('power');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);

  const targets = useMemo(() => {
    if (groups.length === 0) return [{ key: '', label: '', color: 'var(--job-1)' }];
    const list = selected === BOTH ? groups : groups.filter((g) => g.key === selected);
    return list.map((g) => ({ key: g.key, label: g.label, color: groupColor(g.key, g.color) }));
  }, [groups, selected]);

  const load = useCallback(async () => {
    setLoading(true);
    const results = await Promise.all(targets.map(async (t) => ({ t, res: await apiService.getCountryStats(t.key || null) })));
    const ok: CountryPart[] = [];
    const ko: GroupFailure[] = [];
    for (const { t, res } of results) {
      if (res.data) ok.push({ ...t, data: res.data });
      else ko.push({ key: t.key, label: t.label, status: res.status, message: countriesError(res.status, res.error) });
    }
    setParts(ok);
    setFailures(ko);
    setLoading(false);
  }, [targets]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => { setPage(0); }, [sort, query, selected]);

  const live = useMemo(() => (parts ?? []).filter((p) => p.data.available), [parts]);
  const compare = live.length > 1;
  const rows = useMemo(() => sortCountries(filterCountries(tableRows(live), query), sort), [live, sort, query]);

  const selector = groups.length > 0 && (
    <div className={styles.controls}>
      <div className={styles.chips} role="tablist" aria-label="Groupe">
        {groups.map((g) => (
          <button key={g.key} type="button" role="tab" aria-selected={selected === g.key}
            className={`${styles.chip} ${selected === g.key ? styles.chipActive : ''}`} onClick={() => setChoice(g.key)}>
            <span className={styles.swatch} style={{ background: groupColor(g.key, g.color) }} />{g.label}
          </button>
        ))}
        {groups.length > 1 && (
          <button type="button" role="tab" aria-selected={selected === BOTH}
            className={`${styles.chip} ${selected === BOTH ? styles.chipActive : ''}`} onClick={() => setChoice(BOTH)}>
            {groups.map((g) => <span key={g.key} className={styles.swatch} style={{ background: groupColor(g.key, g.color) }} />)}
            Les deux
          </button>
        )}
      </div>
      <button className={lb.buttonGhost} onClick={() => void load()} disabled={loading}><RefreshCw size={16} /> Actualiser</button>
    </div>
  );

  if (loading && !parts) return <div className={styles.section}>{selector}<div className={lb.state}><div className={lb.spinner} /><p>Lecture des pays...</p></div></div>;

  const failureBlock = failures.length > 0 && (
    failures.length === targets.length ? (
      <div className={lb.state}>
        {failures.every((f) => f.status === 404) ? <Info size={32} className={styles.stateIcon} /> : <AlertCircle size={32} />}
        <h3>{failures.every((f) => f.status === 404) ? 'Statistiques des pays pas encore servies' : 'Pays illisibles'}</h3>
        {failures.map((f) => <p key={f.key} className={styles.stateText}>{f.label ? `${f.label} : ` : ''}{f.message}</p>)}
        <button className={lb.buttonGhost} onClick={() => void load()}><RefreshCw size={16} /> Vérifier de nouveau</button>
      </div>
    ) : (
      <div className={styles.info}><AlertCircle size={16} />
        <span>{failures.map((f) => `${f.label} : ${f.message}`).join(' ')}</span>
      </div>
    )
  );
  const unavailable = (parts ?? []).filter((p) => !p.data.available);

  if (live.length === 0) {
    return (
      <div className={styles.section}>
        {selector}
        {failureBlock}
        {unavailable.map((p) => (
          <div key={p.key} className={lb.state}><Flag size={32} /><h3>Pas de pays ici{p.label ? ` (${p.label})` : ''}</h3><p>{p.data.reason}</p></div>
        ))}
      </div>
    );
  }

  // ---------- Séries : un morceau par groupe (un seul hors comparaison) ----------
  const legend = compare ? live.map((p) => ({ id: p.key, label: p.label, color: p.color })) : undefined;
  const seg = (p: CountryPart, value: number, label: string, color: string) =>
    ({ id: p.key || 'v', label: compare ? p.label : label, value, color: compare ? p.color : color });
  const columns = (keys: { key: string; label: string; title: string }[], pick: (p: CountryPart, key: string) => number,
    label: string, color: string): ColumnDatum[] =>
    keys.map((k) => ({ key: k.key, label: k.label, title: k.title, segments: live.map((p) => seg(p, pick(p, k.key), label, color)) }));
  const byDay = (series: (p: CountryPart) => { date: string }[], pick: (p: CountryPart, date: string) => number,
    label: string, color: string): ColumnDatum[] =>
    columns(unionDates(live.map(series)).map((d) => ({ key: d, label: shortDay(d), title: dayLabel(d) })), pick, label, color);
  const has = (pick: (p: CountryPart) => unknown) => live.some((p) => pick(p) != null);

  const sum = (pick: Parameters<typeof total>[1]) => total(live, pick);
  const sub = (pick: Parameters<typeof total>[1], fmt: (n: number) => string = int) => perGroup(live, pick, fmt);
  const one = live[0]!.data;
  const nCountries = sum((d) => d.countries);
  const nMembers = sum((d) => d.members);
  const rosterKnown = has((p) => p.data.roster);
  const playersKnown = has((p) => p.data.players);
  const powerTotal = sum((d) => d.power?.total);
  const bankTotal = sum((d) => d.bank?.total);
  const gdpTotal = sum((d) => d.gdp?.total);
  const gdpKnown = has((p) => p.data.gdp);
  const claimsTotal = sum((d) => d.territory?.claims);
  const rdAvg = nCountries > 0 ? live.reduce((s, p) => s + (p.data.rd?.averageLevel ?? 0) * p.data.countries, 0) / nCountries : 0;
  const rdMax = Math.max(0, ...live.map((p) => p.data.rd?.maxLevel ?? 0));

  const cards = [
    {
      icon: Flag, value: int(nCountries), label: 'Pays fondés',
      sub: compare ? sub((d) => d.countries) : rosterKnown ? `${int(one.roster!.free)} libres sur ${int(one.roster!.listed)} proposés` : `${int(one.openCountries)} ouverts au recrutement`,
    },
    {
      icon: Users, value: int(playersKnown ? sum((d) => d.players?.inCountry) : nMembers), label: 'Joueurs dans un pays',
      sub: compare ? sub((d) => d.players?.inCountry ?? d.members)
        : playersKnown ? `${int(one.players!.withoutCountry)} sans pays, ${dec(one.averageMembers)} par pays` : `${dec(one.averageMembers)} par pays`,
    },
    {
      icon: Zap, value: int(powerTotal), label: 'Power total',
      sub: compare ? sub((d) => d.power?.total) : `${dec(one.power?.average ?? 0)} par pays, ${int(one.power?.max ?? 0)} au plus`,
    },
    {
      icon: MapIcon, value: int(claimsTotal), label: 'Revendications',
      sub: compare ? sub((d) => d.territory?.claims) : `${dec(one.territory?.average ?? 0)} par pays, ${int(one.territory?.houses ?? 0)} maisons`,
    },
    {
      icon: Landmark, value: formatMoneyShort(bankTotal), label: 'En banque',
      sub: compare ? sub((d) => d.bank?.total, formatMoneyShort) : gdpKnown ? `PIB ${formatMoneyShort(gdpTotal)}` : `${formatMoneyShort(one.bank?.average ?? 0)} en moyenne`,
    },
    {
      icon: FlaskConical, value: dec(rdAvg), label: 'R&D moyenne',
      sub: compare ? sub((d) => d.rd?.averageLevel, dec) : `${rdMax} au plus, ${int(one.rd?.researching ?? 0)} en recherche`,
    },
  ];

  const sizeKeys = one.sizes.map((s) => ({ key: s.label, label: s.label, title: `${s.label} membre${s.label === '1' ? '' : 's'}` }));
  const levelMax = rdMax;
  const levelKeys = Array.from({ length: levelMax + 1 }, (_, l) => ({ key: String(l), label: String(l), title: `R&D niveau ${l}` }));
  const playerKeys = [
    { key: 'in', label: 'Dans un pays', title: 'Membres d\'un pays' },
    { key: 'out', label: 'Sans pays', title: 'Joueurs connus sans pays' },
    { key: 'ain', label: 'Actifs avec pays', title: 'Vus ces 7 derniers jours, dans un pays' },
    { key: 'aout', label: 'Actifs sans pays', title: 'Vus ces 7 derniers jours, sans pays' },
  ];
  const playerValue = (p: CountryPart, k: string) => {
    const pl = p.data.players;
    if (!pl) return 0;
    return k === 'in' ? pl.inCountry : k === 'out' ? pl.withoutCountry : k === 'ain' ? pl.activeInCountry : pl.activeWithoutCountry;
  };
  const rosterKeys = [
    { key: 'taken', label: 'Fondés', title: 'Pays fondés' },
    { key: 'free', label: 'Libres', title: 'Pays proposés, pas encore fondés' },
    { key: 'unlisted', label: 'Retirés', title: 'Pays retirés de la liste' },
  ];
  const rosterValue = (p: CountryPart, k: string) => (p.data.roster ? (p.data.roster as Record<string, number>)[k] ?? 0 : 0);
  const ranked = live.some((p) => p.data.leagues.length > 0);
  const anyWar = live.some((p) => p.data.wars);
  const anyUnesco = live.some((p) => p.data.unesco);
  const anyHistory = live.some((p) => p.data.history.length > 0);
  const anyCreated = live.some((p) => p.data.created.length > 0);
  const top = (pick: (r: ReturnType<typeof tableRows>[number]) => number, n = 6) =>
    tableRows(live).filter((r) => pick(r) > 0).sort((a, b) => pick(b) - pick(a) || a.id.localeCompare(b.id)).slice(0, n);
  const label = (r: ReturnType<typeof tableRows>[number]) => r.name ?? r.id;
  const withGroup = (r: ReturnType<typeof tableRows>[number], text: string) => (compare ? `${r.groupLabel}, ${text}` : text);
  const ongoing = live.flatMap((p) => (p.data.wars?.ongoingList ?? []).map((w) => ({ ...w, group: p })))
    .sort((a, b) => b.since - a.since);
  const pages = Math.max(1, Math.ceil(rows.length / PAGE));
  const shown = rows.slice(page * PAGE, page * PAGE + PAGE);
  const sourceText = compare ? `les NGCountryAPI de ${live.map((p) => p.label).join(' et ')}` : `la NGCountryAPI${live[0]!.label ? ` de ${live[0]!.label}` : ''}`;

  return (
    <div className={styles.section}>
      {selector}
      {failureBlock}

      <div className={lb.cards}>
        {cards.map((c) => (
          <div key={c.label} className={lb.card}>
            <div className={lb.cardIcon}><c.icon size={22} strokeWidth={2} /></div>
            <div className={lb.cardText}>
              <span className={lb.cardValue}>{c.value}</span>
              <span className={lb.cardLabel}>{c.label}</span>
              <span className={lb.cardSub}>{c.sub}</span>
            </div>
          </div>
        ))}
      </div>

      <div className={styles.info}><Info size={16} />
        <span>
          Pays fondés, sans Wilderness, SafeZone ni WarZone, lus en direct dans {sourceText}. Le power d'un pays est la
          somme du power de ses membres. Le niveau de recherche et développement (R&D) est la somme des paliers terminés
          de ses cinq branches.
        </span>
      </div>

      <div className={styles.grid2}>
        {rosterKnown && (
          <section className={styles.block}>
            <div className={styles.blockHead}><h3><Flag size={18} /> Pays fondables</h3></div>
            {/* Un anneau par groupe : chaque groupe a son roster, les additionner n'aurait pas de sens. */}
            {live.filter((p) => p.data.roster).map((p) => (
              <div key={p.key || 'v'}>
                {compare && <h4 className={styles.blockSub}><span className={styles.swatch} style={{ background: p.color }} /> {p.label}</h4>}
                <DonutChart slices={rosterKeys.map((k, i) => ({
                  key: k.key, label: k.key === 'unlisted' ? 'Retirés de la liste' : k.label, value: rosterValue(p, k.key),
                  color: ['var(--job-1)', 'var(--job-3)', 'var(--job-other)'][i]!,
                }))} formatValue={countries} centerValue={int(p.data.roster!.total)} centerLabel="pays"
                  size={compare ? 140 : 180} ariaLabel={`Pays fondés, libres et retirés${p.label ? `, ${p.label}` : ''}`} />
              </div>
            ))}
          </section>
        )}
        {playersKnown && (
          <section className={styles.block}>
            <div className={styles.blockHead}><h3><Users size={18} /> Joueurs avec et sans pays</h3></div>
            <ColumnChart data={columns(playerKeys, playerValue, 'Joueurs', 'var(--job-1)')} legend={legend} formatValue={players}
              formatTick={tick} ariaLabel="Joueurs dans un pays et sans pays, et ceux vus ces 7 derniers jours" height={190} />
            <p className={styles.muted}>« Actifs » : vus ces 7 derniers jours. Joueurs connus de NGCountryAPI (déjà venus en jeu).</p>
          </section>
        )}
      </div>

      <div className={styles.grid2}>
        <section className={styles.block}>
          <div className={styles.blockHead}><h3><Users size={18} /> Taille des pays</h3></div>
          <ColumnChart data={columns(sizeKeys, (p, k) => p.data.sizes.find((s) => s.label === k)?.countries ?? 0, 'Pays', 'var(--job-1)')}
            legend={legend} formatValue={countries} formatTick={tick} ariaLabel="Pays par nombre de membres" height={190} />
          <div className={styles.statLine}>
            {live.map((p) => (
              <span key={p.key || 'v'}>{compare ? `${p.label} : ` : ''}médiane <b>{membersFmt(p.data.medianMembers)}</b>, le plus grand <b>{membersFmt(p.data.maxMembers)}</b></span>
            ))}
          </div>
        </section>
        <section className={styles.block}>
          <div className={styles.blockHead}><h3><FlaskConical size={18} /> Recherche et développement (R&D)</h3></div>
          <ColumnChart data={columns(levelKeys, (p, k) => p.data.rd?.levels.find((l) => l.level === Number(k))?.countries ?? 0, 'Pays', 'var(--job-7)')}
            legend={legend} formatValue={countries} formatTick={tick} ariaLabel="Pays par niveau de R&D" height={190} />
          <BarList formatValue={(n) => `palier ${dec(n)}`}
            rows={RD_BRANCH_ORDER.flatMap((b) => live.map((p) => {
              const br = p.data.rd?.branches.find((x) => x.key === b);
              return {
                id: `${b}-${p.key}`, label: compare ? `${rdBranchLabel(b)}, ${p.label}` : rdBranchLabel(b),
                color: compare ? p.color : rdBranchColor(b), value: br?.averageTier ?? 0, share: (br?.averageTier ?? 0) * 10,
                sub: `moyen, ${br?.maxTier ?? 0} au plus`,
              };
            }))} />
        </section>
      </div>

      <div className={styles.grid2}>
        <section className={styles.block}>
          <div className={styles.blockHead}><h3><Zap size={18} /> Power et territoire</h3></div>
          <div className={styles.statLine}>
            <span>Power total <b>{int(powerTotal)}</b></span>
            <span>Revendications <b>{int(claimsTotal)}</b></span>
            <span>dont maisons <b>{int(sum((d) => d.territory?.houses))}</b></span>
          </div>
          <BarList formatValue={int} rows={top((r) => r.power).map((r) => ({
            id: `${r.group}-${r.id}`, label: label(r), color: compare ? r.groupColor : 'var(--job-2)', value: r.power,
            share: powerTotal > 0 ? (r.power * 100) / powerTotal : 0, sub: withGroup(r, `${claimsFmt(r.claims)}, ${membersFmt(r.members)}`),
          }))} />
          {has((p) => p.data.territory?.worlds.length ? p.data.territory : null) && (
            <>
              <h4 className={styles.blockSub}>Revendications par monde</h4>
              <BarList formatValue={int} rows={live.flatMap((p) => (p.data.territory?.worlds ?? []).map((w) => ({
                id: `${p.key}-${w.key}`, label: compare ? `${worldLabel(w.key)}, ${p.label}` : worldLabel(w.key),
                color: compare ? p.color : 'var(--job-3)', value: w.count,
                share: claimsTotal > 0 ? (w.count * 100) / claimsTotal : 0,
              })))} />
            </>
          )}
        </section>
        <section className={styles.block}>
          <div className={styles.blockHead}><h3><Landmark size={18} /> Banque et PIB</h3></div>
          <div className={styles.statLine}>
            <span>En banque <b>{formatMoney(bankTotal)}</b></span>
            {!compare && <span>Médiane <b>{formatMoney(one.bank?.median ?? 0)}</b></span>}
            {!compare && <span>La plus riche <b>{formatMoney(one.bank?.max ?? 0)}</b></span>}
          </div>
          <BarList formatValue={formatMoneyShort} rows={top((r) => r.bank).map((r) => ({
            id: `${r.group}-${r.id}`, label: label(r), color: compare ? r.groupColor : 'var(--job-4)', value: r.bank,
            share: bankTotal > 0 ? (r.bank * 100) / bankTotal : 0, sub: withGroup(r, membersFmt(r.members)),
          }))} />
          {gdpKnown ? (
            <>
              <h4 className={styles.blockSub}>PIB (banque et argent des membres)</h4>
              <div className={styles.statLine}>
                <span>Total <b>{formatMoney(gdpTotal)}</b></span>
                <span>Calculé pour <b>{int(sum((d) => d.gdp?.computed))}</b> pays sur {int(nCountries)}</span>
              </div>
              <BarList formatValue={formatMoneyShort} rows={top((r) => r.gdp ?? 0).map((r) => ({
                id: `gdp-${r.group}-${r.id}`, label: label(r), color: compare ? r.groupColor : 'var(--job-6)', value: r.gdp ?? 0,
                share: gdpTotal > 0 ? ((r.gdp ?? 0) * 100) / gdpTotal : 0, sub: compare ? r.groupLabel : undefined,
              }))} />
            </>
          ) : (
            <p className={styles.muted}>PIB : pas encore de données, NGCountryAPI ne l'a encore calculé pour aucun pays.</p>
          )}
        </section>
      </div>

      {ranked && (
        <section className={styles.block}>
          <div className={styles.blockHead}><h3><Trophy size={18} /> Ligues</h3></div>
          {compare ? (
            <ColumnChart data={columns(one.leagues.map((l) => ({ key: l.key, label: leagueLabel(l.key), title: leagueLabel(l.key) })),
              (p, k) => p.data.leagues.find((l) => l.key === k)?.count ?? 0, 'Pays', 'var(--job-4)')}
              legend={legend} formatValue={countries} formatTick={tick} ariaLabel="Pays par ligue et par groupe" height={180} />
          ) : (
            <DonutChart slices={one.leagues.map((l) => ({ key: l.key, label: leagueLabel(l.key), value: l.count, color: LEAGUE_COLORS[l.key] ?? 'var(--job-other)' }))}
              formatValue={countries} centerValue={int(one.countries)} centerLabel="pays" ariaLabel="Pays par ligue" />
          )}
        </section>
      )}

      <section className={styles.block}>
        <div className={styles.blockHead}><h3><Swords size={18} /> Guerres</h3></div>
        {anyWar ? (
          <>
            <div className={styles.statLine}>
              <span>En cours <b>{int(sum((d) => d.wars?.ongoing))}</b></span>
              <span>Demandées <b>{int(sum((d) => d.wars?.requested))}</b></span>
              <span>Finies <b>{int(sum((d) => d.wars?.ended))}</b></span>
              {compare && <span>{sub((d) => d.wars?.ongoing)} en cours</span>}
            </div>
            <div className={styles.grid2}>
              <div>
                <h4 className={styles.blockSub}>Guerres déclarées par jour (60 derniers jours)</h4>
                <ColumnChart data={byDay((p) => p.data.wars?.declared ?? [], (p, d) => p.data.wars?.declared.find((x) => x.date === d)?.count ?? 0, 'Guerres déclarées', 'var(--job-8)')}
                  legend={legend} formatValue={(n) => plural(n, 'guerre', 'guerres')} formatTick={tick} ariaLabel="Guerres déclarées par jour" height={170} />
              </div>
              <div>
                <h4 className={styles.blockSub}>Comment les guerres se sont finies</h4>
                {sum((d) => d.wars?.ended) === 0 ? <p className={styles.muted}>Aucune guerre finie pour le moment.</p> : (
                  <BarList formatValue={int} rows={(one.wars?.endReasons ?? live.find((p) => p.data.wars)!.data.wars!.endReasons)
                    .filter((r) => live.some((p) => (p.data.wars?.endReasons.find((x) => x.key === r.key)?.count ?? 0) > 0))
                    .flatMap((r) => live.map((p) => {
                      const v = p.data.wars?.endReasons.find((x) => x.key === r.key)?.count ?? 0;
                      const ended = sum((d) => d.wars?.ended);
                      return { id: `${r.key}-${p.key}`, label: compare ? `${warEndLabel(r.key)}, ${p.label}` : warEndLabel(r.key),
                        color: compare ? p.color : 'var(--job-8)', value: v, share: ended > 0 ? (v * 100) / ended : 0 };
                    }))} />
                )}
              </div>
            </div>
            {ongoing.length > 0 && (
              <>
                <h4 className={styles.blockSub}>Guerres en cours</h4>
                <div className={lb.tableWrap}>
                  <table className={lb.table}>
                    <thead><tr>{compare && <th>Groupe</th>}<th>Attaquant</th><th>Défenseur</th><th className={lb.num}>Points</th><th className={lb.num}>Pour gagner</th><th>Depuis le</th></tr></thead>
                    <tbody>
                      {ongoing.slice(0, 10).map((w) => (
                        <tr key={`${w.group.key}-${w.id}`}>
                          {compare && <td><span className={styles.swatch} style={{ background: w.group.color }} /> {w.group.label}</td>}
                          <td>{w.attacker ?? '?'}</td><td>{w.defender ?? '?'}</td>
                          <td className={lb.num}>{int(w.attackerPoints)} contre {int(w.defenderPoints)}</td>
                          <td className={lb.num}>{int(w.pointsToWin)}</td>
                          <td className={lb.nowrap}>{w.since ? formatInstant(w.since).split(' à ')[0] : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </>
        ) : (
          <p className={styles.muted}>Aucune guerre déclarée pour le moment.</p>
        )}
      </section>

      {anyUnesco && (
        <section className={styles.block}>
          <div className={styles.blockHead}><h3><Building2 size={18} /> Sites UNESCO</h3></div>
          <div className={styles.statLine}>
            <span>Acceptés <b>{int(sum((d) => d.unesco?.accepted))}</b></span>
            <span>dont merveilles <b>{int(sum((d) => d.unesco?.wonders))}</b></span>
            <span>En attente du staff <b>{int(sum((d) => d.unesco?.pending))}</b></span>
            <span>Refusés <b>{int(sum((d) => d.unesco?.refused))}</b></span>
          </div>
          <BarList formatValue={int} rows={top((r) => r.unesco).map((r) => ({
            id: `u-${r.group}-${r.id}`, label: label(r), color: compare ? r.groupColor : 'var(--job-5)', value: r.unesco,
            share: sum((d) => d.unesco?.accepted) > 0 ? (r.unesco * 100) / sum((d) => d.unesco?.accepted) : 0, sub: withGroup(r, 'sites acceptés'),
          }))} />
        </section>
      )}

      {(anyCreated || anyHistory) && (
        <section className={styles.block}>
          <div className={styles.blockHead}><h3><FlaskConical size={18} /> Évolution</h3></div>
          <div className={styles.grid2}>
            {anyCreated && (
              <div>
                <h4 className={styles.blockSub}>Pays fondés par jour (pays encore en vie)</h4>
                <ColumnChart data={byDay((p) => p.data.created, (p, d) => p.data.created.find((x) => x.date === d)?.count ?? 0, 'Pays fondés', 'var(--job-3)')}
                  legend={legend} formatValue={countries} formatTick={tick} ariaLabel="Pays fondés par jour" height={180} />
              </div>
            )}
            {anyHistory ? (
              <div>
                <h4 className={styles.blockSub}>Power total, relevé de chaque jour</h4>
                <ColumnChart data={byDay((p) => p.data.history, (p, d) => p.data.history.find((x) => x.date === d)?.power ?? 0, 'Power total', 'var(--job-2)')}
                  legend={legend} formatValue={int} ariaLabel="Power total par jour" height={180} />
              </div>
            ) : (
              <p className={styles.muted}>Le relevé de chaque jour commence avec le satellite 1.0.29 : l'évolution du nombre de pays, du power et de la banque apparaîtra jour après jour.</p>
            )}
          </div>
          {anyHistory && (
            <div className={styles.grid2}>
              <div>
                <h4 className={styles.blockSub}>Pays fondés, relevé de chaque jour</h4>
                <ColumnChart data={byDay((p) => p.data.history, (p, d) => p.data.history.find((x) => x.date === d)?.countries ?? 0, 'Pays', 'var(--job-1)')}
                  legend={legend} formatValue={countries} formatTick={tick} ariaLabel="Pays fondés par jour" height={170} />
              </div>
              <div>
                <h4 className={styles.blockSub}>Banque totale, relevé de chaque jour</h4>
                <ColumnChart data={byDay((p) => p.data.history, (p, d) => p.data.history.find((x) => x.date === d)?.bank ?? 0, 'Banque totale', 'var(--job-4)')}
                  legend={legend} formatValue={formatMoneyShort} ariaLabel="Banque totale par jour" height={170} />
              </div>
            </div>
          )}
        </section>
      )}

      <section className={styles.block}>
        <div className={styles.blockHead}>
          <h3><Flag size={18} /> Tous les pays</h3>
          <span className={lb.tableCount}>{rows.length.toLocaleString('fr-FR')} pays, {pageLabel(page, pages)}</span>
        </div>
        <div className={lb.toolbar}>
          <label className={lb.search}>
            <Search size={16} />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Rechercher un pays par son nom" aria-label="Rechercher un pays" />
            {query && <button type="button" className={lb.clear} onClick={() => setQuery('')} title="Effacer la recherche"><X size={14} /></button>}
          </label>
          <select className={lb.select} value={sort} onChange={(e) => setSort(e.target.value as CountrySort)} aria-label="Trier par">
            {SORTS.map((s) => <option key={s.key} value={s.key}>Tri : {s.label}</option>)}
          </select>
        </div>
        <div className={styles.miniJobs} aria-label="Branches de la R&D">
          <span className={styles.muted}>Branches de la R&D :</span>
          {RD_BRANCH_ORDER.map((b) => <span key={b}><span className={styles.swatch} style={{ background: rdBranchColor(b) }} />{rdBranchLabel(b)}</span>)}
        </div>
        {shown.length === 0 ? <p className={lb.empty}>{query ? 'Aucun pays de ce nom.' : 'Aucun pays fondé pour le moment.'}</p> : (
          <div className={lb.tableWrap}>
            <table className={lb.table}>
              <thead>
                <tr>
                  <th>#</th><th>Pays</th>{compare && <th>Groupe</th>}<th className={lb.num}>Membres</th><th className={lb.num}>Power</th>
                  <th className={lb.num}>Territoire</th><th className={lb.num}>R&D</th><th>Branches</th><th className={lb.num}>Banque</th>
                  <th className={lb.num}>PIB</th><th className={lb.num}>Guerres</th>{anyUnesco && <th className={lb.num}>UNESCO</th>}
                  {ranked && <th>Ligue</th>}<th>Fondé le</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((r, i) => {
                  const rank = page * PAGE + i + 1;
                  const m = !query ? medal(rank) : null;
                  return (
                    <tr key={`${r.group}-${r.id}`}>
                      <td><span className={`${lb.rank} ${m ? lb[m] : ''}`}>{rank}</span></td>
                      <td>
                        <span className={lb.playerName}>{r.name ?? r.id}</span>
                        {r.country && <span className={lb.playerSub}> {r.country}</span>}
                        {r.open && <span className={lb.playerSub}> ouvert</span>}
                      </td>
                      {compare && <td className={lb.nowrap}><span className={styles.swatch} style={{ background: r.groupColor }} /> {r.groupLabel}</td>}
                      <td className={lb.num}>{r.members}</td>
                      <td className={lb.num}>{int(r.power)}</td>
                      <td className={lb.num} title={`${r.houses} maison${r.houses > 1 ? 's' : ''}`}>{int(r.claims)}</td>
                      <td className={lb.num}><span className={styles.levelPill}>{r.rdLevel}</span></td>
                      <td>
                        <div className={styles.miniJobs} style={{ flexWrap: 'nowrap' }}>
                          {RD_BRANCH_ORDER.map((b) => (
                            <span key={b} title={`${rdBranchLabel(b)} : palier ${r.branches[b] ?? 0}`}>
                              <span className={styles.swatch} style={{ background: rdBranchColor(b) }} />{r.branches[b] ?? 0}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className={lb.num}>{formatMoney(r.bank)}</td>
                      <td className={lb.num}>{r.gdp != null ? formatMoneyShort(r.gdp) : '-'}</td>
                      <td className={lb.num} title={`${r.warsOngoing} en cours, ${r.warsWon} gagnées sur ${r.warsEnded} finies`}>
                        {[r.warsOngoing > 0 ? `${r.warsOngoing} en cours` : '', r.warsWon > 0 ? `${r.warsWon} gagnée${r.warsWon > 1 ? 's' : ''}` : '']
                          .filter(Boolean).join(', ') || '-'}
                      </td>
                      {anyUnesco && <td className={lb.num}>{r.unesco || '-'}</td>}
                      {ranked && <td>{leagueLabel(r.league)}</td>}
                      <td className={lb.nowrap}>{r.createdAt ? formatInstant(r.createdAt).split(' à ')[0] : '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {pages > 1 && (
          <div className={lb.pagination}>
            <button className={lb.buttonGhost} onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}><ChevronLeft size={16} /> Précédent</button>
            <span>{pageLabel(page, pages)}</span>
            <button className={lb.buttonGhost} onClick={() => setPage((p) => p + 1)} disabled={page + 1 >= pages}>Suivant <ChevronRight size={16} /></button>
          </div>
        )}
      </section>
    </div>
  );
}
